import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BookOpen } from 'lucide-react';
import { SUPPORT_CHANNELS, type SupportFeedbackKind } from '@repo/shared-types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@repo/ui/src/components/ui/dialog';
import { Button } from '@repo/ui/src/components/ui/button';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { Separator } from '@repo/ui/src/components/ui/separator';
import { BusyButton } from '../BusyButton';
import { useHelpDialogStore } from '../../stores/helpDialogStore';
import { useSupportFeedback } from '../../hooks/useSupportFeedback';

const KINDS: SupportFeedbackKind[] = ['bug', 'feature', 'question'];
const MAX_LENGTH = 2000;
const MIN_LENGTH = 10;

// Same origin the invoice/legal links use (apps/web, a different origin from
// this SPA) — see the (deleted) SupportBubble for the same fallback.
const GUIDE_URL = `${import.meta.env.VITE_WEB_URL || 'http://localhost:5173'}/huong-dan`;

/**
 * "Trợ giúp & góp ý" — a feedback form (POST /support/feedback) plus the
 * human contact channels and the user guide, all in one place reachable from
 * the sidebar footer (desktop/tablet) and the Thêm sheet's user block
 * (phones) — see `helpDialogStore`.
 */
export function HelpDialog() {
  const { t } = useTranslation('common');
  const open = useHelpDialogStore((s) => s.open);
  const setOpen = useHelpDialogStore((s) => s.setOpen);
  const [kind, setKind] = useState<SupportFeedbackKind | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<'tooFast' | 'error' | null>(null);
  const feedback = useSupportFeedback();

  const trimmedLength = message.trim().length;
  const canSend = !!kind && trimmedLength >= MIN_LENGTH && !feedback.isPending;

  function reset() {
    setKind(null);
    setMessage('');
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset();
  }

  function handleSend() {
    if (!kind || !canSend) return;
    setError(null);
    feedback.mutate(
      { kind, message: message.trim(), page: window.location.pathname },
      {
        onSuccess: () => {
          toast.success(t('help.sent'));
          setOpen(false);
          reset();
        },
        onError: (err) => {
          setError(err.status === 429 || err.code === 'RATE_LIMITED' ? 'tooFast' : 'error');
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('help.title')}</DialogTitle>
          <DialogDescription>{t('help.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => (
              <Button
                key={k}
                type="button"
                variant={kind === k ? 'default' : 'outline'}
                size="sm"
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
              >
                {t(`help.kind.${k}`)}
              </Button>
            ))}
          </div>

          <div className="space-y-1">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('help.placeholder')}
              aria-label={t('help.messageLabel')}
              maxLength={MAX_LENGTH}
              rows={4}
            />
            <div className="flex justify-end text-xs text-muted-foreground tabular-nums">
              {message.length}/{MAX_LENGTH}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{t(`help.${error}`)}</p>}

          <BusyButton
            type="button"
            className="w-full"
            disabled={!canSend}
            busy={feedback.isPending}
            busyLabel={t('help.sending')}
            onClick={handleSend}
          >
            {t('help.send')}
          </BusyButton>
        </div>

        <Separator />

        <div className="space-y-1">
          <p className="text-sm font-medium text-surface-800">{t('help.contactTitle')}</p>
          <ul>
            {SUPPORT_CHANNELS.map((c) => (
              <li key={c.href}>
                <a
                  href={c.href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium text-surface-800 transition-colors hover:bg-surface-100"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 shrink-0 text-surface-400" aria-hidden="true">
                    <path d={c.path} />
                  </svg>
                  {c.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href={GUIDE_URL}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded-lg px-2 py-2.5 text-sm font-medium text-surface-800 transition-colors hover:bg-surface-100"
              >
                <BookOpen className="h-5 w-5 shrink-0 text-surface-400" aria-hidden="true" />
                {t('help.guide')}
              </a>
            </li>
          </ul>
          <p className="px-2 text-xs text-surface-400">{t('help.invite')}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
